import { useCallback, useEffect, useRef, useState } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import apiService from '../services/api';
import '../styles/StaffPages.css';

/**
 * Fan Intelligence Data Portal — pick a school, then one tab per initiative.
 *
 * Each tab lists that initiative's insights. Every insight carries its own
 * score, its data sources (from the school or from Homecrowd) with what we
 * have so far, and a couple of suggestions that would raise it most. School
 * data is added right on the row that uses it. Scores come from the
 * server (services/data_portal.py); this page only shows them.
 */

const NAVY = '#001C44';

const card = {
  backgroundColor: 'white',
  padding: '24px',
  borderRadius: '12px',
  marginBottom: '16px',
  border: '1px solid #d1d5db',
};

const text = {
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  color: '#374151',
  lineHeight: 1.5,
};

const label = {
  ...text,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#6b7280',
};

const row = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px',
  backgroundColor: '#f3f4f6',
  borderRadius: '8px',
  gap: '14px',
};

const button = {
  padding: '7px 14px',
  backgroundColor: 'white',
  color: '#111827',
  border: '1px solid #9ca3af',
  borderRadius: '8px',
  fontFamily: '"Baikal Book"',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const primaryButton = { ...button, backgroundColor: NAVY, color: 'white', border: `1px solid ${NAVY}` };

const bandColor = (score) => {
  if (score < 25) return '#991b1b';
  if (score < 50) return '#b45309';
  if (score < 75) return '#1d4ed8';
  return '#047857';
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

function Pills({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            style={{
              padding: '12px 24px',
              backgroundColor: active ? NAVY : 'white',
              border: `1px solid ${active ? NAVY : '#9ca3af'}`,
              borderRadius: '100px',
              fontFamily: '"Baikal Book"',
              fontSize: '15px',
              color: active ? 'white' : '#111827',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SourceTag({ kind }) {
  const school = kind === 'school';
  return (
    <span
      style={{
        ...text,
        fontSize: 11,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 999,
        backgroundColor: school ? NAVY : '#e5e7eb',
        color: school ? 'white' : '#111827',
        flexShrink: 0,
        minWidth: 96,
        textAlign: 'center',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
      }}
    >
      {school ? 'School' : 'Homecrowd'}
    </span>
  );
}

function AddPanel({ need, busy, onSubmit, onCancel }) {
  const [mode, setMode] = useState('file');
  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState('');
  const [notes, setNotes] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const canSubmit = mode === 'file' ? Boolean(file) : pasted.trim().length > 0;

  const submit = () => {
    if (!canSubmit || busy) return;
    if (mode === 'file') {
      onSubmit(file, notes);
      return;
    }
    // Spreadsheet pastes arrive tab-separated; the server sniffs the delimiter.
    const blob = new Blob([pasted], { type: 'text/csv' });
    onSubmit(new File([blob], `${need.key}-pasted.csv`, { type: 'text/csv' }), notes);
  };

  const pasteRows = pasted.trim() ? pasted.trim().split(/\r?\n/).length : 0;

  const modePill = (value, labelText) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      style={{
        ...button,
        padding: '6px 14px',
        backgroundColor: mode === value ? NAVY : 'white',
        color: mode === value ? 'white' : '#111827',
        border: `1px solid ${mode === value ? NAVY : '#9ca3af'}`,
        borderRadius: 999,
      }}
    >
      {labelText}
    </button>
  );

  return (
    <div style={{ padding: '14px 16px 16px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 8 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {modePill('file', 'Upload a file')}
        {modePill('paste', 'Paste rows')}
      </div>

      {mode === 'file' ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const dropped = e.dataTransfer?.files?.[0];
            if (dropped) setFile(dropped);
          }}
          style={{
            ...text,
            padding: '22px 16px',
            textAlign: 'center',
            border: `2px dashed ${dragOver ? NAVY : '#9ca3af'}`,
            borderRadius: 8,
            backgroundColor: dragOver ? '#eef2ff' : '#fafafa',
            cursor: 'pointer',
            color: '#111827',
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.xls,.json,.zip,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              e.target.value = '';
              if (chosen) setFile(chosen);
            }}
          />
          {file ? (
            <>
              <b>{file.name}</b>
              <div style={{ ...text, fontSize: 12 }}>{(file.size / 1024).toFixed(0)} KB · click to choose a different file</div>
            </>
          ) : (
            <>
              <b>Drop a file here</b> or click to choose
              <div style={{ ...text, fontSize: 12 }}>CSV, TSV, Excel, JSON, ZIP, or PDF · up to 50 MB</div>
            </>
          )}
        </div>
      ) : (
        <>
          <textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder={'Paste rows straight from a spreadsheet or CSV — include the header row.\nfan_id\tgame\tspend\n1001\tG1\t50'}
            rows={6}
            style={{
              ...text,
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              border: '1px solid #9ca3af',
              borderRadius: 8,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              color: '#111827',
              resize: 'vertical',
            }}
          />
          <div style={{ ...text, fontSize: 12, marginTop: 4 }}>
            {pasteRows > 1 ? `${(pasteRows - 1).toLocaleString()} rows + header` : 'Nothing pasted yet'}
          </div>
        </>
      )}

      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Note (optional) — e.g. FY26 export from Paciolan, through Sept 15"
        style={{
          ...text,
          width: '100%',
          boxSizing: 'border-box',
          marginTop: 12,
          padding: '9px 12px',
          border: '1px solid #9ca3af',
          borderRadius: 8,
          color: '#111827',
        }}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          style={{ ...primaryButton, opacity: canSubmit && !busy ? 1 : 0.5, cursor: canSubmit && !busy ? 'pointer' : 'not-allowed' }}
          disabled={!canSubmit || busy}
          onClick={submit}
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button type="button" style={button} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function NeedRow({ need, busy, onUpload, onRemove }) {
  const [open, setOpen] = useState(false);
  const latest = need.latest_upload;
  const isSchool = need.kind === 'school';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={row}>
        <SourceTag kind={need.kind} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...text, color: '#111827', fontWeight: 600 }} title={need.description || ''}>
            {need.label}
          </div>
          <div style={{ ...text, fontSize: 12 }}>
            {isSchool
              ? latest
                ? `✓ ${latest.original_filename}${latest.row_count != null ? ` · ${Number(latest.row_count).toLocaleString()} rows` : ''}${latest.created_at ? ` · ${formatDate(latest.created_at)}` : ''}${latest.stored ? '' : ' · file not stored'}${latest.notes ? ` · ${latest.notes}` : ''}`
                : 'Not provided yet'
              : `${Number(need.value).toLocaleString()} of ${Number(need.target).toLocaleString()}`}
          </div>
        </div>
        <div style={{ ...text, fontWeight: 700, color: bandColor(need.coverage), width: 44, textAlign: 'right', flexShrink: 0 }}>
          {need.coverage}%
        </div>
        {isSchool && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              style={open ? button : need.provided ? button : primaryButton}
              disabled={busy}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? 'Close' : need.provided ? 'Replace' : '+ Add'}
            </button>
            {latest && !open && (
              <button type="button" style={button} disabled={busy} onClick={() => onRemove(latest.id)}>
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      {isSchool && open && (
        <AddPanel
          need={need}
          busy={busy}
          onCancel={() => setOpen(false)}
          onSubmit={async (file, notes) => {
            const ok = await onUpload(need.key, file, notes);
            if (ok) setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function InsightCard({ insight, busyKey, onUpload, onRemove }) {
  const color = bandColor(insight.score);
  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ textAlign: 'center', minWidth: 64 }}>
          <div style={{ fontFamily: '"Baikal Bold", "Baikal Book"', fontSize: 36, fontWeight: 700, color, lineHeight: 1 }}>
            {insight.score}
          </div>
          <div style={{ ...text, fontSize: 12, color, fontWeight: 600, marginTop: 4 }}>{insight.score_label}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...text, fontSize: 16, color: '#111827', fontWeight: 600, lineHeight: 1.35 }}>
            {insight.text}
          </div>
          {insight.suggestions.length > 0 && (
            <div style={{ ...text, marginTop: 8 }}>
              <b style={{ color: '#111827' }}>To raise this: </b>
              {insight.suggestions.map((s, index) => (
                <span key={s.key}>
                  {index > 0 && ' · '}
                  {s.action} <b style={{ color: '#047857' }}>+{s.gain}</b>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...label, margin: '18px 0 8px' }}>Data sources</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {insight.needs.map((need) => (
          <NeedRow
            key={`${need.kind}-${need.key}`}
            need={need}
            busy={busyKey === need.key}
            onUpload={onUpload}
            onRemove={onRemove}
          />
        ))}
      </div>
    </div>
  );
}

export default function SuperuserDataPortal() {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [portal, setPortal] = useState(null);
  const [activeTab, setActiveTab] = useState('revenue');
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, me] = await Promise.all([
          apiService.getSchools(),
          apiService.fetchCurrentUser().catch(() => null),
        ]);
        if (cancelled) return;
        // getSchools() hands back a bare array or a paginated {results} — normalize both.
        const rows = Array.isArray(list) ? list : list?.results || [];
        const sorted = [...rows].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
        setSchools(sorted);
        const activeId = me?.active_school?.id || me?.active_school;
        setSelectedSchool(sorted.find((s) => String(s.id) === String(activeId)) || sorted[0] || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || 'Could not load schools.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadPortal = useCallback(async (school) => {
    if (!school) return;
    setPortalLoading(true);
    setError('');
    try {
      setPortal(await apiService.getDataPortal(school.id));
    } catch (e) {
      setPortal(null);
      setError(e?.response?.data?.error || 'Could not load the data portal.');
    } finally {
      setPortalLoading(false);
    }
  }, []);

  useEffect(() => { loadPortal(selectedSchool); }, [selectedSchool, loadPortal]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const handleUpload = useCallback(async (datasetKey, file, notes = '') => {
    if (!selectedSchool) return false;
    setBusyKey(datasetKey);
    setError('');
    try {
      const out = await apiService.uploadDataPortalFile(selectedSchool.id, datasetKey, file, notes);
      const rows = out?.upload?.row_count;
      setNotice(
        `${file.name} received${rows != null ? ` · ${Number(rows).toLocaleString()} rows` : ''}` +
          (out?.stored === false ? ' (file not stored — storage unavailable)' : '')
      );
      await loadPortal(selectedSchool);
      return true;
    } catch (e) {
      setError(e?.response?.data?.error || 'Upload failed.');
      return false;
    } finally {
      setBusyKey(null);
    }
  }, [selectedSchool, loadPortal]);

  const handleRemove = useCallback(async (uploadId) => {
    if (!selectedSchool) return;
    if (!window.confirm('Remove this upload?')) return;
    setError('');
    try {
      await apiService.deleteDataPortalUpload(selectedSchool.id, uploadId);
      setNotice('Upload removed.');
      await loadPortal(selectedSchool);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not remove the upload.');
    }
  }, [selectedSchool, loadPortal]);

  const initiatives = portal?.initiatives || [];
  const current = initiatives.find((i) => i.key === activeTab) || initiatives[0];

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Data Portal</h1>
          <p className="staff-page-subheader">
            Each insight is scored on its data sources. Add the school's data on the rows that use
            it; Homecrowd's own data fills in as fans use the app.
          </p>

          {error && <div className="toast toast-error" style={{ marginTop: 16 }}>{error}</div>}
          {notice && <div className="toast toast-success" style={{ marginTop: 16 }}>{notice}</div>}

          <div style={{ marginTop: 24, maxWidth: 1000 }}>
            <SchoolPicker schools={schools} value={selectedSchool} onChange={setSelectedSchool} />

            {initiatives.length > 0 && (
              <div style={{ margin: '20px 0 8px' }}>
                <Pills
                  value={current?.key}
                  onChange={setActiveTab}
                  options={initiatives.map((i) => ({ value: i.key, label: i.title }))}
                />
              </div>
            )}

            {current && !portalLoading && (
              <p style={{ ...text, margin: '12px 0 20px' }}>{current.summary}</p>
            )}

            {portalLoading && (
              <div style={{ ...card, ...text, textAlign: 'center', marginTop: 16 }}>Scoring {selectedSchool?.name}…</div>
            )}

            {current && !portalLoading && current.insights.map((insight) => (
              <InsightCard
                key={insight.key}
                insight={insight}
                busyKey={busyKey}
                onUpload={handleUpload}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
